import { Menu } from "@headlessui/react";
import type { JSX } from "react";
import { useTheme } from "@/theme/themeContext";
import type { ThemePreference } from "@/theme/themeContext";
import { classNames } from "@/lib/format";

const OPTIONS: { value: ThemePreference; label: string; icon: JSX.Element }[] = [
  { value: "light", label: "Hell", icon: <SunIcon /> },
  { value: "dark", label: "Dunkel", icon: <MoonIcon /> },
  { value: "system", label: "System", icon: <SystemIcon /> },
];

export type ThemeToggleVariant = "default" | "inverse";

interface ThemeToggleProps {
  variant?: ThemeToggleVariant;
}

export function ThemeToggle({ variant = "default" }: ThemeToggleProps) {
  const { preference, effective, setPreference } = useTheme();
  // OPTIONS enthält "system" als Fallback an Index 2 → kann nie undefined werden.
  const current = (OPTIONS.find((o) => o.value === preference) ?? OPTIONS[2]) as (typeof OPTIONS)[number];

  // Beide Varianten sehen im Apple-Look gleich aus – nur in der inverse-Variante
  // (z. B. auf einem getönten Header) bekommt der Button etwas mehr Kontrast.
  const buttonClass = classNames(
    "inline-flex h-9 w-9 items-center justify-center rounded-full transition",
    variant === "inverse"
      ? "text-label hover:bg-fill"
      : "border border-separator bg-card text-label hover:bg-fill-tertiary",
  );

  return (
    <Menu as="div" className="relative">
      <Menu.Button
        className={buttonClass}
        aria-label={`Theme: ${current.label}${preference === "system" ? ` (aktuell ${effective})` : ""}`}
        title={`Theme: ${current.label}`}
      >
        {effective === "dark" ? <MoonIcon /> : <SunIcon />}
      </Menu.Button>
      <Menu.Items
        className="absolute right-0 z-50 mt-2 w-44 origin-top-right rounded-ios border border-separator bg-card p-1 shadow-ios-md ring-1 ring-black/5 focus:outline-none"
      >
        {OPTIONS.map((opt) => (
          <Menu.Item key={opt.value}>
            {({ active }) => (
              <button
                type="button"
                onClick={() => setPreference(opt.value)}
                className={classNames(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-label",
                  active ? "bg-fill-tertiary" : "bg-transparent",
                  preference === opt.value && "font-medium text-accent",
                )}
              >
                <span className="flex h-5 w-5 items-center justify-center text-current">
                  {opt.icon}
                </span>
                <span>{opt.label}</span>
                {preference === opt.value ? (
                  <span className="ml-auto text-xs text-label-tertiary">aktiv</span>
                ) : null}
              </button>
            )}
          </Menu.Item>
        ))}
      </Menu.Items>
    </Menu>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
    </svg>
  );
}

function SystemIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  );
}
